import React, { useState } from "react";
import { Check, LogOut, PanelLeftClose, PanelLeftOpen, UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "src/contexts/AuthContext";
import { useSidebar } from "src/contexts/SidebarContext";
import { cn } from "src/utils/cn";
import { Button } from "src/components/ui/button";
import { OrgAvatar } from "src/components/sidebar/OrgAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "src/components/ui/tooltip";

export function SidebarWorkspaceSwitcher() {
  const { organization, organizations, currentUser, switchOrganization, logout } = useAuth();
  const { collapsed, toggleCollapsed } = useSidebar();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);

  const workspaceName = organization?.name ?? "Clozdl";

  // Developers belong to a single organization and cannot switch orgs.
  // Brokers / channel partners can be members of multiple orgs and switch
  // between them from this menu.
  const canSwitchOrg = currentUser?.account_type !== "developer";

  async function handleSwitch(orgId) {
    if (!canSwitchOrg || orgId === organization?.id || switching) return;
    setSwitching(true);
    try {
      await switchOrganization(orgId);
      navigate("/dashboard");
    } finally {
      setSwitching(false);
    }
  }

  async function handleLogout() {
    await logout();
  }

  return (
    <div className="flex h-8 items-center justify-between gap-1">
      {/* ── Org switcher trigger ──────────────────────────────────────── */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            disabled={switching}
            className={cn(
              "flex h-auto min-w-0 items-center gap-2 rounded-md px-2 py-1 text-left font-normal transition-colors",
              "hover:bg-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4b5bed]/40",
              "data-[state=open]:bg-white/80 data-[state=open]:ring-2 data-[state=open]:ring-[#4b5bed]/40",
              switching && "opacity-60 cursor-not-allowed",
              collapsed ? "w-10 justify-center" : "flex-1"
            )}
          >
            <OrgAvatar org={organization} size="md" />
            {!collapsed && (
              <span className="flex min-w-0 flex-1 items-center gap-1">
                <span className="truncate text-sm font-semibold text-[#24243a]">
                  {workspaceName}
                </span>
                {canSwitchOrg && (
                  <svg
                    className="ml-0.5 h-3.5 w-3.5 shrink-0 text-slate-500"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 6l4 4 4-4" />
                  </svg>
                )}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          side="bottom"
          align="start"
          sideOffset={6}
          className="w-64 p-1.5"
        >
          {/* ── Organization list ────────────────────────────────────── */}
          {!canSwitchOrg ? (
            // Developers belong to a single org: show it without a checkmark
            // and without switching behaviour. Not `disabled` so it stays at
            // full opacity — it is simply non-interactive.
            <DropdownMenuItem
              onSelect={(e) => e.preventDefault()}
              className="flex cursor-default items-center gap-3 rounded-lg px-2.5 py-2.5 text-[14px] font-semibold focus:bg-transparent"
            >
              <OrgAvatar org={organization} size="lg" />
              <span className="min-w-0 flex-1 truncate">{workspaceName}</span>
            </DropdownMenuItem>
          ) : organizations.length > 0 ? (
            organizations.map((org) => {
              const isActive = org.id === organization?.id;
              return (
                <DropdownMenuItem
                  key={org.id}
                  onSelect={() => handleSwitch(org.id)}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2.5 text-[14px]",
                    isActive && "font-semibold"
                  )}
                >
                  <OrgAvatar org={org} size="lg" />
                  <span className="min-w-0 flex-1 truncate">{org.name}</span>
                  {isActive && (
                    <Check className="ml-auto h-4 w-4 shrink-0 text-[#4b5bed]" />
                  )}
                </DropdownMenuItem>
              );
            })
          ) : (
            <DropdownMenuItem disabled className="px-2.5 py-2 text-[13px] text-slate-400">
              No other organizations
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator className="my-1.5" />

          {/* ── User email (non-interactive) ─────────────────────────── */}
          <DropdownMenuLabel className="px-2.5 py-1 text-[12px] font-normal text-slate-400 truncate">
            {currentUser?.email}
          </DropdownMenuLabel>

          {/* ── Invite Members ───────────────────────────────────────── */}
          <DropdownMenuItem
            onSelect={() => navigate("/configurations/invitations")}
            className="flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2.5 text-[14px] font-medium text-slate-700"
          >
            <UserPlus className="h-4 w-4 text-slate-500" />
            Invite Members
          </DropdownMenuItem>

          {/* ── Logout ───────────────────────────────────────────────── */}
          <DropdownMenuItem
            onSelect={handleLogout}
            className="flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2.5 text-[14px] font-medium text-rose-600 focus:bg-rose-50 focus:text-rose-700"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* ── Collapse toggle ──────────────────────────────────────────── */}
      {!collapsed && <div className="h-5 w-px bg-slate-200" />}

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={toggleCollapsed}
            className="h-auto w-auto rounded-md p-1.5 text-slate-500 hover:bg-white hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4b5bed]/40"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <PanelLeftClose className="h-4 w-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">
          <span className="flex items-center gap-2">
            {collapsed ? "Expand" : "Collapse"}
            <span className="rounded bg-slate-800 px-1 py-0.5 text-[10px]">⌘E</span>
          </span>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
